import { Avatar, Box, Typography, IconButton } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { capitalizeEachWords, getInitials } from "../../utils/helpers";
import { useState } from "react";

import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { renderBlock } from "./discussionUtils/commentsRenderBlock";
import CommentsImageDialog from "./commentsDialog/CommentsImageFileDialog";
import FilePreviewDialog from "./commentsDialog/FilePreviewDialog";
import CommentBox from "./CommentBox";
import { convertTiptapNodesToHTML } from "./discussionUtils/discussionHelpers";
import DeleteConfirmDialog from "./commentsDialog/DeleteConfirmDialog";
import {
  setCommentObjectID,
  setDeleteLoader,
  setEditingCommentId,
  setIsExpandCommentBox,
} from "../../features/genericCommentsAndFilesSlice";
import { useDispatch } from "react-redux";
import EmojiReactions from "./emoji/EmojiReactions";
import { Stack } from "@mui/system";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../features/userSlice";
import { DiscussionCommentsAndFilesEndPointLogic } from "../../logic/GeneralDiscussion/DiscussionCommentsAndFilesEndPointLogic";

type Reaction = {
  id: string; // e.g. "wink"
  native: string; // e.g. "😉"
  userIds: string[]; // which users gave this emoji
  removedUserIds?: (string | number)[];
  commentDetails?: any;
};
const MessageItem = ({
  user,
  timestamp,
  contentBlocks,
  commentId,
  object_id,
  handleUpdateComment,
  handleDeleteComment,
  canChange,
  particularReaction,
  commentDetails,
}: any) => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const [reactions, setReactions] = useState<Reaction[]>([]); // State for reactions emojis
  const editingCommentId = useSelector(
    (state: any) => state.genericCommentsAndFiles.editingCommentId
  );
  const [commentToDelete, setCommentToDelete] = useState<string | null>("");
  const [commentValue, setCommentValue] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // State for the delete dialog
  const [openImage, setOpenImage] = useState<string | null>(null); // State for the image dialog
  const deleteLoader = useSelector(
    (state: any) => state.genericCommentsAndFiles.deleteLoader
  );
  const [openFile, setOpenFile] = useState<{
    src: string;
    filename: string;
  } | null>(null);

  const handleCloseFile = () => setOpenFile(null);

  // Function to open the image dialog
  const handleImageClick = (src: string) => {
    setOpenImage(src);
  };

  // Function to close the image dialog
  const handleClose = () => {
    setOpenImage(null);
    dispatch(setCommentObjectID("")); // clear object id
  };

  // Function to open the delete confirmation dialog
  const openDeleteDialog = (id: string) => {
    setCommentToDelete(id);
    dispatch(setCommentObjectID(object_id)); // set the object id of the comment to be deleted
    setDeleteDialogOpen(true);
  };

  // Function to handle confirm delete
  const handleConfirmDelete = async () => {
    if (!commentToDelete) return;
    //  dispatch(setCommentsLoader(true)); // Show loader before delete
    dispatch(setDeleteLoader(true));
    try {
      await handleDeleteComment(commentToDelete); // Calls the delete ep from useComments
      setCommentToDelete("");
    } catch (error) {
      console.error("Failed to delete comment", error);
    } finally {
      dispatch(setCommentObjectID("")); // clear object id
      setDeleteDialogOpen(false);
      dispatch(setDeleteLoader(false));
      // dispatch(setCommentsLoader(false)); //Hide loader after fetch completes
    }
  };
  /**
   * Handles a user's reaction toggle on a comment (add or remove).
   *
   * This function is **purely local / optimistic**:
   * - It does NOT read any server reaction state.
   * - It only updates the `reactions` state, which is a "diff" layer on top of
   *   the server data that `EmojiReactions` already merges for display.
   *
   * Behavior:
   * - If `isRemoving` is true:
   *    - Ensure the current user is removed from `userIds` for that emoji.
   *    - Append the current user to `removedUserIds` so the UI can override
   *      any stale server data that still shows this reaction.
   *    - If no local entry exists yet, a new one is created with:
   *         { id, native, userIds: [], removedUserIds: [userId] }.
   *
   * - If `isRemoving` is false (adding):
   *    - Ensure the current user is present in `userIds` for that emoji.
   *    - Remove the current user from `removedUserIds` (if they had previously
   *      un-reacted).
   *    - If no local entry exists yet, a new one is created with:
   *         { id, native, userIds: [userId] }.
   *
   * After updating local state, it calls the backend endpoint to persist
   * the toggle (`updateSingleComment`). The UI does not wait for the API
   * response; the visual change is immediate (optimistic update).
   *
   * @async
   * @function handleReact
   * @param {string} reactionId - The unique id of the emoji (e.g., "laugh", "heart").
   * @param {string} native - The native emoji character (e.g., "😂").
   * @param {boolean} isRemoving - Whether the merged UI state indicates the user is currently reacting (true = remove, false = add).
   * @returns {Promise<void>}
   */
  const handleReact = async (
    reactionId: string,
    native: string,
    isRemoving: boolean
  ) => {
    const userId = user?.id;
    if (!userId) return;

    setReactions((prev) => {
      const existing = prev.find((r) => r.id === reactionId);

      if (isRemoving) {
        if (existing) {
          const updatedUserIds = existing.userIds
            .map(String)
            .filter((id) => id !== String(userId));

          return prev.map((r) =>
            r.id === reactionId
              ? {
                  ...r,
                  userIds: updatedUserIds,
                  removedUserIds: [...(r.removedUserIds || []), userId],
                }
              : r
          );
        } else {
          return [
            ...prev,
            {
              id: reactionId,
              native,
              userIds: [],
              removedUserIds: [userId],
            } as any,
          ];
        }
      } else {
        if (existing) {
          const updatedRemovedUserIds = (existing.removedUserIds || []).filter(
            (id: any) => String(id) !== String(userId)
          );

          const allUserIds = [...existing.userIds.map(String), String(userId)];
          const uniqueUserIds = Array.from(new Set(allUserIds)).map((id) =>
            isNaN(Number(id)) ? id : Number(id)
          );

          return prev.map((r) =>
            r.id === reactionId
              ? {
                  ...r,
                  userIds: uniqueUserIds,
                  removedUserIds: updatedRemovedUserIds,
                }
              : r
          );
        } else {
          return [
            ...prev,
            {
              id: reactionId,
              native,
              userIds: [userId],
            } as any,
          ];
        }
      }
    });

    try {
      const body = {
        comment_id: commentId,
        reaction_id: reactionId,
      };

      await DiscussionCommentsAndFilesEndPointLogic.updateSingleComment(
        oauthTokenHeader,
        body,
        object_id
      );
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <>
      <Box sx={{ display: "flex", gap: 1 }}>
        {/* <Avatar src={user.avatarUrl} alt={user.name} /> */}
        <Avatar
          sx={{ bgcolor: "#7B1FA2", width: 24, height: 24, fontSize: 12 }}
        >
          {getInitials(user.name)}
        </Avatar>
        <Box flex={1}>
          <Stack direction="row" alignItems={"center"} gap={1}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "400px", color: "rgba(0, 0, 0, 0.6)" }}
            >
              {capitalizeEachWords(user.name)}{" "}
            </Typography>
            <Typography color="text.disabled">•</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ color: "rgba(0, 0, 0, 0.38)" }}
            >
              {timestamp}
            </Typography>
          </Stack>
          {/* Body section (Comment Lists) */}
          <Box sx={{ mt: 1, typography: "body2" }}>
            {editingCommentId === commentId ? (
              <CommentBox
                commentId={commentId}
                defaultValue={commentValue}
                onCancel={() => {
                  setEditingCommentId(null);
                  dispatch(setEditingCommentId("")); // set editing state
                  dispatch(setCommentObjectID("")); // clear object id
                  dispatch(setIsExpandCommentBox(false));
                  setCommentValue("");
                }}
                onSave={(updatedValue: any) => {
                  setCommentValue(updatedValue);
                  //setEditingCommentId(null);
                  dispatch(setEditingCommentId("")); // set editing state
                  dispatch(setCommentObjectID("")); // clear object id
                  dispatch(setIsExpandCommentBox(false));
                }}
                handleUpdateComment={handleUpdateComment}
              />
            ) : (
              <Box
                sx={{
                  typography: "body2",
                  wordBreak: "break-word", // ensures long strings break
                  overflowWrap: "anywhere", // alternative for wide support
                  whiteSpace: "pre-wrap", // preserves line breaks
                }}
              >
                {contentBlocks?.map((block: any, i: number) =>
                  renderBlock(block, i, {
                    handleImageClick: handleImageClick,
                    onFileClick: (fileData: any) => setOpenFile(fileData),
                  })
                )}
              </Box>
            )}
          </Box>

          {/* Footer section */}
          <Box sx={{ display: "flex", gap: 1, mt: 1, color: "gray" }}>
            <EmojiReactions
              reactions={reactions}
              onReact={(reactionId, native, isRemoving) =>
                handleReact(reactionId, native, isRemoving)
              }
              currentUserId={user?.id}
              particularReaction={particularReaction}
              commentDetails={commentDetails}
            />

            {canChange && (
              <>
                {/* { this for edit comment} */}
                <IconButton
                  size="small"
                  onClick={() => {
                    // setEditingCommentId(commentId);
                    dispatch(setCommentObjectID(object_id));
                    dispatch(setEditingCommentId(commentId)); // set editing state
                    dispatch(setIsExpandCommentBox(false));
                    setCommentValue(convertTiptapNodesToHTML(contentBlocks)); // Convert HTML to plain textcomments); // Prefill original value
                  }}
                >
                  <EditNoteOutlinedIcon fontSize="small" />
                </IconButton>

                {/* { this for delete comment} */}
                <IconButton
                  size="small"
                  onClick={() => openDeleteDialog(commentId)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* This is the image dialog when click on the image */}
      <CommentsImageDialog
        open={!!openImage}
        src={openImage}
        onClose={handleClose}
      />

      {/* this is the file like pdf/csv dialog */}
      {/* PDF/File Preview Dialog */}
      <FilePreviewDialog
        open={!!openFile}
        src={openFile?.src}
        filename={openFile?.filename}
        onClose={handleCloseFile}
      />

      {/* Delete Confirm Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        deleteLoader={deleteLoader}
        onDelete={handleConfirmDelete}
        deleteFrom="comment"
      />
    </>
  );
};

export default MessageItem;
